import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Award, Shield, ThumbsUp, DollarSign } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { getVehicles } from '../services/api';

export const Home = () => {
  const [featuredVehicles, setFeaturedVehicles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadVehicles = async () => {
      try {
        const response = await getVehicles({ sort: 'newest' });
        setFeaturedVehicles(response.data.slice(0, 3));
      } catch (error) {
        console.error('Error loading vehicles:', error);
      } finally {
        setLoading(false);
      }
    };
    
    loadVehicles();
  }, []);

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-black py-24 md:py-40 overflow-hidden">
        {/* Metallic overlay effect */}
        <div className="absolute inset-0 bg-gradient-to-r from-teal-600/10 to-transparent"></div>
        <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-teal-500/5 to-transparent"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-5xl md:text-7xl font-black text-white mb-6 tracking-tight">
              Affordable Luxury Vehicles
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-teal-400 via-teal-500 to-teal-600 mt-3">
                For Everyone
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 mb-10 font-medium">
              Over <span className="text-teal-400 font-bold">15 years</span> of providing quality pre-owned luxury vehicles in Marietta, Georgia
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/inventory">
                <Button size="lg" className="bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 text-white px-10 py-7 text-lg font-bold shadow-xl shadow-teal-500/50 border-2 border-teal-400">
                  View Inventory
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/contact">
                <Button size="lg" variant="outline" className="border-3 border-teal-400 text-white hover:bg-teal-600 hover:border-teal-500 px-10 py-7 text-lg font-bold shadow-xl">
                  Contact Us
                </Button>
              </Link>
            </div>
          </div>
        </div>
        
        {/* Bottom gradient fade */}
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-white to-transparent"></div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl md:text-5xl font-black text-center text-gray-900 mb-4">
            Why Choose <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-500 to-teal-600">Legacy Motorplex</span>?
          </h2>
          <p className="text-center text-gray-600 text-lg mb-16 max-w-2xl mx-auto">
            Experience the difference of working with Marietta's most trusted luxury vehicle dealership
          </p>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <Card className="border-2 border-teal-500/20 shadow-lg hover:shadow-2xl hover:border-teal-500 transition-all group">
              <CardContent className="pt-8 text-center">
                <div className="bg-gradient-to-br from-teal-500 to-teal-600 w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg shadow-teal-500/50 group-hover:scale-110 transition-transform">
                  <Award className="h-10 w-10 text-white" />
                </div>
                <h3 className="text-xl font-bold mb-3 text-gray-900">15+ Years Experience</h3>
                <p className="text-gray-600 font-medium">Trusted expertise in luxury vehicles</p>
              </CardContent>
            </Card>

            <Card className="border-2 border-gray-300/50 shadow-lg hover:shadow-2xl hover:border-gray-400 transition-all group">
              <CardContent className="pt-8 text-center">
                <div className="bg-gradient-to-br from-gray-700 to-gray-900 w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg shadow-gray-500/50 group-hover:scale-110 transition-transform">
                  <Shield className="h-10 w-10 text-white" />
                </div>
                <h3 className="text-xl font-bold mb-3 text-gray-900">Quality Guaranteed</h3>
                <p className="text-gray-600 font-medium">Thoroughly inspected vehicles</p>
              </CardContent>
            </Card>

            <Card className="border-2 border-teal-500/20 shadow-lg hover:shadow-2xl hover:border-teal-500 transition-all group">
              <CardContent className="pt-8 text-center">
                <div className="bg-gradient-to-br from-teal-500 to-teal-600 w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg shadow-teal-500/50 group-hover:scale-110 transition-transform">
                  <DollarSign className="h-10 w-10 text-white" />
                </div>
                <h3 className="text-xl font-bold mb-3 text-gray-900">Affordable Luxury</h3>
                <p className="text-gray-600 font-medium">Premium vehicles at great prices</p>
              </CardContent>
            </Card>

            <Card className="border-2 border-gray-300/50 shadow-lg hover:shadow-2xl hover:border-gray-400 transition-all group">
              <CardContent className="pt-8 text-center">
                <div className="bg-gradient-to-br from-gray-700 to-gray-900 w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg shadow-gray-500/50 group-hover:scale-110 transition-transform">
                  <ThumbsUp className="h-10 w-10 text-white" />
                </div>
                <h3 className="text-xl font-bold mb-3 text-gray-900">Customer First</h3>
                <p className="text-gray-600 font-medium">Your satisfaction is our priority</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Featured Vehicles */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-4">
              Featured <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-500 to-teal-600">Vehicles</span>
            </h2>
            <p className="text-gray-600 text-xl font-medium">Discover our handpicked selection of luxury vehicles</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {loading ? (
              <div className="col-span-3 text-center py-12">
                <p className="text-gray-600">Loading vehicles...</p>
              </div>
            ) : featuredVehicles.length === 0 ? (
              <div className="col-span-3 text-center py-12">
                <p className="text-gray-600">No vehicles available</p>
              </div>
            ) : (
              featuredVehicles.map((vehicle) => (
                <Card key={vehicle.id} className="overflow-hidden hover:shadow-2xl transition-all border-2 border-transparent hover:border-teal-500 group">
                  <div className="aspect-video overflow-hidden relative">
                    <img 
                      src={vehicle.images[0]} 
                      alt={`${vehicle.year} ${vehicle.make} ${vehicle.model}`}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
                  </div>
                  <CardContent className="p-6 bg-white">
                    <div className="flex justify-between items-start mb-2">
                      <h3 className="text-2xl font-black text-gray-900">
                        {vehicle.year} {vehicle.make}
                      </h3>
                      <span className="bg-gradient-to-r from-teal-500 to-teal-600 text-white px-4 py-2 rounded-full text-sm font-bold shadow-lg shadow-teal-500/50">
                        ${vehicle.price.toLocaleString()}
                      </span>
                    </div>
                    <p className="text-gray-600 mb-4 font-semibold">{vehicle.model}</p>
                    <div className="flex justify-between text-sm text-gray-600 mb-6 font-medium">
                      <span>{vehicle.mileage.toLocaleString()} miles</span>
                      <span className="text-teal-600 font-semibold">{vehicle.condition}</span>
                    </div>
                    <Link to={`/vehicle/${vehicle.id}`}>
                      <Button className="w-full bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 text-white font-bold py-6 shadow-lg shadow-teal-500/30">
                        View Details
                        <ArrowRight className="ml-2 h-5 w-5" />
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              ))
            )}
          </div>

          <div className="text-center mt-16">
            <Link to="/inventory">
              <Button size="lg" className="bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 text-white font-bold px-10 py-7 text-lg shadow-xl shadow-teal-500/50 border-2 border-teal-400">
                View All Vehicles
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-12">
            What Our Customers Say
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card className="border-none shadow-md">
              <CardContent className="pt-6">
                <div className="flex mb-4">
                  {[...Array(5)].map((_, i) => (
                    <svg key={i} className="w-5 h-5 text-yellow-400 fill-current" viewBox="0 0 20 20">
                      <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
                    </svg>
                  ))}
                </div>
                <p className="text-gray-700 mb-4">"Amazing experience! Found the perfect BMW and the team made the process so easy. Highly recommend Legacy Motorplex!"</p>
                <p className="font-semibold text-gray-900">Sarah Johnson</p>
              </CardContent>
            </Card>
            
            <Card className="border-none shadow-md">
              <CardContent className="pt-6">
                <div className="flex mb-4">
                  {[...Array(5)].map((_, i) => (
                    <svg key={i} className="w-5 h-5 text-yellow-400 fill-current" viewBox="0 0 20 20">
                      <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
                    </svg>
                  ))}
                </div>
                <p className="text-gray-700 mb-4">"Best dealership in Georgia! They helped me find an incredible Mercedes at a great price. Professional and trustworthy."</p>
                <p className="font-semibold text-gray-900">Michael Chen</p>
              </CardContent>
            </Card>
            
            <Card className="border-none shadow-md">
              <CardContent className="pt-6">
                <div className="flex mb-4">
                  {[...Array(5)].map((_, i) => (
                    <svg key={i} className="w-5 h-5 text-yellow-400 fill-current" viewBox="0 0 20 20">
                      <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
                    </svg>
                  ))}
                </div>
                <p className="text-gray-700 mb-4">"15+ years in business shows! Quality vehicles and excellent customer service. Got my Lexus here and love it!"</p>
                <p className="font-semibold text-gray-900">Jennifer Williams</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-gray-900 via-teal-900 to-gray-900 relative overflow-hidden">
        {/* Metallic shine effect */}
        <div className="absolute inset-0 bg-gradient-to-br from-teal-500/20 via-transparent to-teal-500/20"></div>
        <div className="absolute top-0 left-1/4 w-1/2 h-full bg-gradient-to-b from-teal-400/10 to-transparent blur-3xl"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-4xl md:text-6xl font-black text-white mb-6">
            Ready to Find Your <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-400 to-teal-300">Dream Car</span>?
          </h2>
          <p className="text-xl md:text-2xl text-gray-300 mb-10 max-w-2xl mx-auto font-medium">
            Visit our showroom or call us today to schedule a test drive
          </p>
          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <a href="tel:4047231672">
              <Button size="lg" className="bg-gradient-to-r from-teal-400 to-teal-500 hover:from-teal-500 hover:to-teal-600 text-gray-900 font-black px-10 py-7 text-xl shadow-2xl shadow-teal-500/50 border-2 border-teal-300">
                Call (404) 723-1672
              </Button>
            </a>
            <Link to="/contact">
              <Button size="lg" variant="outline" className="border-3 border-teal-400 text-white hover:bg-teal-500 hover:text-gray-900 font-bold px-10 py-7 text-xl shadow-2xl">
                Get Directions
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};
